import type {
  Currency,
  EconomySummary,
  LeaderboardSnapshot,
} from '@shared/types';
import { api } from './api';

/**
 * Admin economy + leaderboard API client. Wraps the REST endpoints introduced
 * in epic #281 / sub-issue #287:
 *
 *   - GET /admin/economy/summary?from=&to=&currency=  → `EconomySummary`
 *   - GET /admin/leaderboard/snapshot?limit=          → `LeaderboardSnapshot`
 *
 * Both endpoints require `adminRole >= 'viewer'`, which is enforced server-side.
 * The admin SPA gate (`<RequireAdmin minRole="viewer">` in `App.tsx`) prevents
 * non-admin browsers from reaching these screens in the first place.
 */

export interface EconomySummaryParams {
  from: string; // YYYY-MM-DD inclusive
  to: string;   // YYYY-MM-DD inclusive
  currency?: Currency | 'all';
}

/** Build a query string, skipping empty / undefined values. */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function getEconomySummary(
  params: EconomySummaryParams,
): Promise<EconomySummary> {
  const qs = toQuery({
    from: params.from,
    to: params.to,
    // Only forward `currency` when the admin has picked a concrete filter —
    // `'all'` is the server default, so omitting it keeps URLs shorter and
    // the endpoint idempotent to the absence/presence of the key.
    currency:
      params.currency && params.currency !== 'all' ? params.currency : undefined,
  });
  return api<EconomySummary>(`/admin/economy/summary${qs}`);
}

export async function getLeaderboardSnapshot(
  limit = 100,
): Promise<LeaderboardSnapshot> {
  const qs = toQuery({ limit });
  return api<LeaderboardSnapshot>(`/admin/leaderboard/snapshot${qs}`);
}
